// Web Speech voice selection, shared between the announcer (which speaks) and
// the Settings panel (which lets you choose). The chosen voice is a local
// preference keyed by the voice's URI; when it's absent or unavailable we fall
// back to an auto-pick heuristic.

export const VOICE_PREF_KEY = 'yoga-me.voice.v1';

const isEn = (v: SpeechSynthesisVoice) => /^en(-|_|$)/i.test(v.lang);

/** English voices (the cues are English), sorted by name; all voices if none. */
export function listVoices(): SpeechSynthesisVoice[] {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return [];
  const all = window.speechSynthesis.getVoices();
  const en = all.filter(isEn);
  return (en.length ? en : all).slice().sort((a, b) => a.name.localeCompare(b.name));
}

/** The voice we'd choose automatically: prefer OS-flagged high-quality ones. */
export function autoPick(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  if (!voices.length) return null;
  const enhanced = /(natural|neural|premium|enhanced|siri|ava|samantha|allison|google)/i;
  return (
    voices.find((v) => isEn(v) && enhanced.test(v.name) && !/compact/i.test(v.name)) ||
    voices.find((v) => /en[-_]US/i.test(v.lang) && !/compact/i.test(v.name)) ||
    voices.find(isEn) ||
    voices[0]
  );
}

// The saved choice is one of: automatic (default, best natural voice available),
// a pre-rendered clip voice (Polly), or a system Web Speech voice. Stored as a
// prefixed string in localStorage: "" = auto, "clip:<id>", "speech:<voiceURI>".
export type VoiceChoice =
  | { kind: 'auto' }
  | { kind: 'clip'; id: string }
  | { kind: 'speech'; uri: string };

export function getVoiceChoice(): VoiceChoice {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(VOICE_PREF_KEY);
  } catch {
    /* ignore */
  }
  if (!raw) return { kind: 'auto' };
  if (raw.startsWith('clip:')) return { kind: 'clip', id: raw.slice(5) };
  if (raw.startsWith('speech:')) return { kind: 'speech', uri: raw.slice(7) };
  return { kind: 'speech', uri: raw }; // legacy bare voiceURI
}

export function setVoiceChoice(choice: VoiceChoice): void {
  try {
    if (choice.kind === 'auto') localStorage.removeItem(VOICE_PREF_KEY);
    else if (choice.kind === 'clip') localStorage.setItem(VOICE_PREF_KEY, `clip:${choice.id}`);
    else localStorage.setItem(VOICE_PREF_KEY, `speech:${choice.uri}`);
  } catch {
    /* ignore */
  }
}

/** Serialize a choice to the value used by the Settings <select>. */
export function choiceValue(choice: VoiceChoice): string {
  if (choice.kind === 'auto') return '';
  if (choice.kind === 'clip') return `clip:${choice.id}`;
  return `speech:${choice.uri}`;
}

/** The Web Speech voice to use: honor a saved system-voice choice, else auto-pick. */
export function resolveVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  const choice = getVoiceChoice();
  const saved = choice.kind === 'speech' ? voices.find((v) => v.voiceURI === choice.uri) : null;
  return saved ?? autoPick(voices);
}
